import { Link as RouterLink } from "react-router-dom";
import React, { FC, MouseEventHandler, ReactNode } from "react";

type Color = "blue" | "red" | "white" | "yellow";
interface Props {
  title?: string;
  children?: ReactNode;
  className?: string;
  color?: Color;
  disabled?: boolean;
  icon?: any;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  radius?: "full" | "regular";
  size?: Size;
  tabIndex?: number;
  type?: "button" | "submit";
}

type Size = "regular" | "small";

export const Button: FC<Props> & { Link: typeof Link } = ({
  children,
  className,
  color = "blue",
  disabled = false,
  icon,
  onClick,
  radius = "regular",
  size = "regular",
  tabIndex,
  title,
  type = "button",
}) => {
  const classNames = styles({
    children,
    className,
    color,
    disabled,
    icon,
    radius,
    size,
  });

  return (
    <button
      className={classNames}
      disabled={disabled}
      onClick={onClick}
      tabIndex={tabIndex}
      title={title}
      type={type}
    >
      {children}
    </button>
  );
};

const Link: FC<
  Omit<Props, "disabled" | "onClick" | "tabIndex" | "type"> & {
    to: string;
  }
> = ({
  children,
  className,
  color = "blue",
  icon,
  radius = "regular",
  size = "regular",
  to,
}) => {
  const classNames = styles({
    children,
    className,
    color,
    disabled: false,
    icon,
    radius,
    size,
  });

  return (
    <RouterLink className={classNames} to={to}>
      {children}
    </RouterLink>
  );
};

Button.Link = Link;

function color_styles(color: Color, disabled: boolean): string {
  if (disabled) {
    return "bg-gray-000 text-gray-400 cursor-not-allowed";
  }

  return {
    blue:
      "bg-blue-700 text-white border border-blue-700 hover:bg-blue-800 hover:border-blue-800 focus:outline-none focus:border-blue-700 focus:shadow-outline",
    red:
      "bg-red-700 text-white border border-red-700 hover:bg-red-800 hover:border-red-800 focus:outline-none focus:border-blue-700 focus:shadow-outline",
    white:
      "bg-white text-gray-700 border border-gray-200 hover:bg-gray-000 hover:border-gray-000 focus:outline-none focus:border-blue-700 focus:shadow-outline",
    yellow:
      "bg-yellow-800 text-white border border-yellow-800 hover:bg-yellow-900 hover:border-yellow-900 focus:outline-none focus:border-blue-700 focus:shadow-outline",
  }[color];
}

function size_styles(
  size: Size,
  hasIcon: boolean,
  hasChildren: boolean
): string {
  if (hasIcon && !hasChildren) {
    return {
      regular: "p-4",
      small: "p-2",
    }[size];
  }

  if (hasIcon && hasChildren) {
    return {
      regular: "pl-4 pr-6 py-4",
      small: "pl-3 pr-6 py-3",
    }[size];
  }

  return {
    regular: "px-6 py-4",
    small: "px-4 py-2 text-sm",
  }[size];
}

function styles({
  children,
  className,
  color = "blue",
  disabled = false,
  icon,
  radius,
  size = "regular",
}: Props) {
  const sizeStyles = size_styles(size, !!icon, !!children);
  const colorStyles = color_styles(color, disabled);

  return `select-none inline-flex justify-center items-center leading-tighter transition ${
    radius === "full" ? "rounded-full" : "rounded-lg"
  } ${sizeStyles} ${colorStyles} ${className}`;
}
